import { FormField } from 'ui/formField';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTakeoverExperimentContext } from 'modules/experiments/contexts';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import { TestStrategy } from 'modules/experiments/types';
import {
  StyledExperimentGroupSizeGrid,
  StyledSmallerInputText,
  StyledAttributionCheckboxGridSection,
  StyledAttributionSizeGridSection,
  StyledFields,
  StyledErrorMessage,
  StyledTestStrategySectionText,
  StyledRadioListWrapper,
  StyledSecondaryOptionLabel,
} from 'modules/experiments/components/pages/edit/sections/settings/styled';
import { UpdateExperimentSettingsFormFieldNames } from 'modules/experiments/constants/updateExperimentSettings';
import {
  DEFAULT_MIN_SENDS_PER_VARIANT,
  MIN_SENDS_PER_VARIANT,
} from 'modules/experiments/takeover/constants';
import { RadioListField } from 'lib/iterable-hook-form';
import { HelpToggletip } from 'ui/tooltip';

import { getRadioFieldOptions } from '../helpers';

export const AudienceFormTriggerSubsetSetting = () => {
  const { t } = useExperimentsTranslations();
  const { TEST_SENDS_PER_VARIATION, HAS_HOLDOUT_GROUP, TRIGGERED_TEST_STRATEGY } =
    UpdateExperimentSettingsFormFieldNames;

  const {
    formState: { errors },
    setValue,
  } = useFormContext();
  const {
    campaignSchedulerMsgParams: { scheduleType },
  } = useTakeoverExperimentContext();

  const [triggeredTestStrategy] = useWatch({
    name: [TRIGGERED_TEST_STRATEGY],
  });

  // Edge case where holdout has a value and prevent validation for System Events to be ran
  setValue(HAS_HOLDOUT_GROUP, false);

  const showAttributionSize = triggeredTestStrategy === TestStrategy.SUBSET_USERS;

  return (
    <StyledExperimentGroupSizeGrid
      data-test="trigger-exp-subset"
      showAttributionSize={showAttributionSize}
    >
      <StyledTestStrategySectionText>
        {t('settings.settingsLabels.testStrategy')}
      </StyledTestStrategySectionText>

      <StyledAttributionCheckboxGridSection>
        <StyledRadioListWrapper data-test="trigger-send-to-remainder">
          <RadioListField
            name={TRIGGERED_TEST_STRATEGY}
            options={getRadioFieldOptions(t, scheduleType)}
            isRegularWeight={true}
          />
        </StyledRadioListWrapper>
      </StyledAttributionCheckboxGridSection>

      {showAttributionSize && (
        <StyledAttributionSizeGridSection>
          <StyledFields>
            <StyledSecondaryOptionLabel data-test="min-sends-per-variant">
              {t('settings.minSendsPerVariant.label')}
              <HelpToggletip tooltipContent={t('settings.minimumSendsTooltip')} />
            </StyledSecondaryOptionLabel>
            <FormField isHorizontalLayout inputId="min-sends-per-variant">
              <StyledSmallerInputText
                name={TEST_SENDS_PER_VARIATION}
                valueAsNumber={true}
                type="number"
                data-test={'exp-group-size-input'}
                id="exp-group-size-input"
                minValue={MIN_SENDS_PER_VARIANT}
                placeholder={`${DEFAULT_MIN_SENDS_PER_VARIANT}`}
                onKeyDown={(e) => {
                  const { key } = e;
                  // Prevent user from entering decimal values
                  if (
                    (key < '0' || key > '9') &&
                    !['Delete', 'Backspace', 'ArrowLeft', 'ArrowRight'].includes(key)
                  ) {
                    e.preventDefault();
                  }
                }}
              />
            </FormField>
          </StyledFields>
          {!!errors[TEST_SENDS_PER_VARIATION] && (
            <StyledErrorMessage>
              {/* @ts-expect-error */}
              {errors[TEST_SENDS_PER_VARIATION]?.message}
            </StyledErrorMessage>
          )}
        </StyledAttributionSizeGridSection>
      )}
    </StyledExperimentGroupSizeGrid>
  );
};
